import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
} from 'react';
import { useState } from 'react';
import { DateRange } from 'react-date-range';
import { AiTwotoneCalendar } from 'react-icons/ai';
import {
  Box,
  Text,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  BoxProps,
  Button,
  VStack,
  StackDivider,
  useDisclosure,
} from '@chakra-ui/react';
import {
  setGlobalDate,
  useAppDispatch,
  updatesGlobalDate,
} from '@spartalabs/pdc-core';
import { addDays } from 'date-fns';
import format from 'date-fns/format';
import { pt } from 'date-fns/locale';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { colors } from 'theme/components/colors';
import { checkDateRange } from 'utils/functions';
import MonthPicker from './components/monthPicker';

interface DateRangeCompProps extends BoxProps {
  startDate?: DateProps[];
  type?: string;
  isOnHeader?: boolean;
  dateName?: string;
}

const DateRangeComp = forwardRef<DateProps[], DateRangeCompProps>(
  (props, ref) => {
    const dispatch = useAppDispatch();

    const { startDate, type, isOnHeader, dateName, ...rest } = props;
    const [pickedRange, setPickedRange] = useState<DateProps[]>(
      startDate || [
        {
          startDate: new Date(),
          endDate: addDays(new Date(), 10),
          key: 'selection',
        },
      ],
    );

    useEffect(() => {
      if (startDate) setPickedRange(startDate);
    }, [startDate]);

    const [range, setRange] = useState<DateProps[]>(
      startDate || [
        {
          startDate: new Date(),
          endDate: addDays(new Date(), 10),
          key: 'selection',
        },
      ],
    );

    const handleDate = useCallback((type: string) => {
      const date = new Date();
      switch (type) {
        case 'atual':
          return setRange([
            {
              startDate: new Date(date.getFullYear(), date.getMonth(), 1),
              endDate: new Date(),
              key: 'selection',
            },
          ]);
        case 'lastMonth':
          return setRange([
            {
              startDate: new Date(date.getFullYear(), date.getMonth() - 1, 1),
              endDate: new Date(),
              key: 'selection',
            },
          ]);
        case 'last3Months':
          return setRange([
            {
              startDate: new Date(date.getFullYear(), date.getMonth() - 3, 1),
              endDate: new Date(),
              key: 'selection',
            },
          ]);
        case 'last6Months':
          return setRange([
            {
              startDate: new Date(date.getFullYear(), date.getMonth() - 6, 1),
              endDate: new Date(),
              key: 'selection',
            },
          ]);
        case 'lastYear':
          return setRange([
            {
              startDate: new Date(date.getFullYear() - 1, date.getMonth(), 1),
              endDate: new Date(),
              key: 'selection',
            },
          ]);
        case 'last3Days':
          return setRange([
            {
              startDate: addDays(new Date(), -3),
              endDate: new Date(),
              key: 'selection',
            },
          ]);
        case 'lastWeek':
          return setRange([
            {
              startDate: addDays(new Date(), -7),
              endDate: new Date(),
              key: 'selection',
            },
          ]);

        default:
          return;
      }
    }, []);

    const menu = useDisclosure();

    useImperativeHandle(ref, () => pickedRange ?? new Date(), [pickedRange]);

    return (
      <Box {...rest}>
        <Menu {...menu}>
          <MenuButton
            data-cy={`date-${dateName}`}
            transition="all 0.3s"
            _focus={{ boxShadow: 'none' }}
          >
            <Flex
              alignItems="center"
              p="4px"
              border="1px solid"
              borderColor="gray.900"
              borderRadius="4px"
            >
              <AiTwotoneCalendar size={14} color={colors.gray[100]} />

              <Text
                data-cy={`date-text-${dateName}`}
                ml="9px"
                fontSize="xs"
                color={isOnHeader ? 'white' : 'text.100'}
              >
                {`${format(
                  checkDateRange(pickedRange)[0]?.startDate || new Date(),
                  'dd/MM/yyyy',
                )} ~ ${format(
                  checkDateRange(pickedRange)[0]?.endDate || new Date(),
                  'dd/MM/yyyy',
                )}`}
              </Text>
            </Flex>
            <Box display={{ base: 'flex', xl: 'none' }}></Box>
          </MenuButton>

          <MenuList overflow="hidden" bg="white">
            <Box display="flex" flexDirection="row" mt="-60px">
              {type === 'Dia' ? (
                <DateRange
                  locale={pt}
                  ranges={range}
                  onChange={item => setRange([item.selection])}
                />
              ) : (
                <>
                  <MonthPicker
                    name={dateName}
                    range={range}
                    setRange={setRange}
                  />
                </>
              )}
              <Box>
                <VStack
                  h="80%"
                  borderLeft="1px solid gray"
                  borderColor="gray.200"
                  alignItems="start"
                  justifyContent="initial"
                  px="16px"
                  mt="60px"
                  divider={<StackDivider borderColor="gray.200" />}
                >
                  {type === 'Dia' ? (
                    <VStack
                      alignItems="start"
                      divider={<StackDivider borderColor="gray.200" />}
                    >
                      <Text
                        data-cy={`3d-${dateName}`}
                        as="button"
                        onClick={() => handleDate('last3Days')}
                        mt="4px"
                        fontSize="sm"
                        color="text.100"
                      >
                        Últimos 3 dias
                      </Text>
                      <Text
                        data-cy={`7d-${dateName}`}
                        as="button"
                        onClick={() => handleDate('lastWeek')}
                        fontSize="sm"
                        color="text.100"
                      >
                        Últimos 7 dias
                      </Text>
                    </VStack>
                  ) : (
                    <VStack
                      alignItems="start"
                      divider={<StackDivider borderColor="gray.200" />}
                    >
                      <Text
                        as="button"
                        onClick={() => handleDate('atual')}
                        mt="4px"
                        fontSize="sm"
                        color="text.100"
                      >
                        Mês atual
                      </Text>
                      <Text
                        as="button"
                        onClick={() => handleDate('lastMonth')}
                        fontSize="sm"
                        color="text.100"
                      >
                        Último mês
                      </Text>
                      <Text
                        as="button"
                        onClick={() => handleDate('last3Months')}
                        fontSize="sm"
                        color="text.100"
                      >
                        Últimos 3 meses
                      </Text>
                      <Text
                        as="button"
                        onClick={() => handleDate('last6Months')}
                        fontSize="sm"
                        color="text.100"
                      >
                        Últimos 6 meses
                      </Text>
                      <Text
                        as="button"
                        onClick={() => handleDate('lastYear')}
                        fontSize="sm"
                        color="text.100"
                      >
                        Últimos 12 meses
                      </Text>
                    </VStack>
                  )}
                  <Button
                    data-cy={`date-aply-${dateName}`}
                    onClick={() => {
                      setPickedRange(range);

                      if (isOnHeader) {
                        dispatch(setGlobalDate(range));
                        dispatch(updatesGlobalDate(true));
                      }

                      menu.onClose();
                    }}
                    mt="auto"
                  >
                    Aplicar
                  </Button>
                </VStack>
              </Box>
            </Box>
          </MenuList>
        </Menu>
      </Box>
    );
  },
);

DateRangeComp.displayName = 'DateRangeComp';

export default DateRangeComp;
